/**
 * Centralized status configuration for the application
 * Following OCP principle - extend by adding to configs, not modifying code
 * Colors aligned with Medley Design System (see /docs/STYLE_GUIDE.md)
 */

import type { CoachStatus, CohortStatus, MilestoneStatus, RsvpStatus } from '../types';
import type {
  CohortCoachAvailabilityStatus,
  ScheduleCellState,
} from '../services/api/cohortCoaches';

export interface StatusConfig {
  label: string;
  color: string;
  variant?: 'filled' | 'light' | 'outline';
}

// Coach Status Configuration — MED-683: derived status (Active/Inactive)
export const COACH_STATUS_CONFIG: Record<CoachStatus, StatusConfig> = {
  active: { label: 'Active', color: 'medleyGreen', variant: 'light' },
  inactive: { label: 'Inactive', color: 'gray', variant: 'light' },
};

// RSVP / attendance status — MED-1044: shared so the session RSVP pill renders
// identically everywhere. Previously duplicated inline in RsvpSection and
// WorkshopSessionInfoTab; the coach prep roster (GroupRosterCard) is the third
// consumer, so the config lives here instead of being copied a third time.
// "Awaiting" covers the pending / no-RSVP-yet state.
export const RSVP_STATUS_CONFIG: Record<RsvpStatus, StatusConfig> = {
  accepted: { label: 'Accepted', color: 'green', variant: 'light' },
  declined: { label: 'Declined', color: 'red', variant: 'light' },
  tentative: { label: 'Tentative', color: 'yellow', variant: 'light' },
  pending: { label: 'Awaiting', color: 'gray', variant: 'light' },
};

export const COACH_STATUS_OPTIONS = [
  { value: '', label: 'All Coaches' },
  ...Object.entries(COACH_STATUS_CONFIG).map(([value, config]) => ({
    value,
    label: config.label,
  })),
];

// Cohort Status Configuration - MED-160/MED-161: Updated per design spec
// Colors: Draft #E8E6E3, In Design #8B5CF6, In Prep #C1DBEF, Live #F09A65,
// Wrap Up #28463A, Completed #4CAF50, Cancelled/Archived #888888
export const COHORT_STATUS_CONFIG: Record<CohortStatus, StatusConfig> = {
  draft: { label: 'Draft', color: 'statusDraft', variant: 'filled' },
  in_design: { label: 'In Design', color: 'surveyPurple', variant: 'filled' },
  in_prep: { label: 'In Prep', color: 'statusInPrep', variant: 'filled' },
  live: { label: 'Live', color: 'statusLive', variant: 'filled' },
  wrap_up: { label: 'Wrap Up', color: 'medleyGreen', variant: 'filled' },
  completed: { label: 'Completed', color: 'statusCompleted', variant: 'filled' },
  cancelled: { label: 'Cancelled', color: 'statusCancelled', variant: 'filled' }, // MED-161: #888888
  archived: { label: 'Archived', color: 'statusCancelled', variant: 'filled' }, // MED-161: #888888
};

export const COHORT_STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  ...Object.entries(COHORT_STATUS_CONFIG).map(([value, config]) => ({
    value,
    label: config.label,
  })),
];

// Session Status Configuration
// Note: 'in_progress' is a computed display status for live sessions (not in SessionStatus type)
// MED-1007 (E4.0g): 'missed' is a terminal no-show state for peer-led Live Session
// Delivery sessions — rendered as a distinct amber "MISSED" badge (visually
// separate from completed/cancelled), signalling it needs rescheduling.
export const SESSION_STATUS_CONFIG: Record<string, StatusConfig> = {
  completed: { label: 'COMPLETED', color: 'medleyGreen', variant: 'filled' },
  in_progress: { label: 'LIVE', color: 'dangerRed', variant: 'filled' },
  scheduled: { label: 'UPCOMING', color: 'blue', variant: 'filled' },
  cancelled: { label: 'CANCELLED', color: 'gray', variant: 'filled' },
  missed: { label: 'MISSED', color: 'warningAmber', variant: 'filled' },
};

// Session Content Status Configuration — MED-1075 / E1.3k
// Left-side content-authoring badge on the Sessions tab. Surfaces states
// defined elsewhere (agenda e1.3d, pre-session work e1.3f, over-length e1.3j).
// Text + color (Mantine light), no leading icon except 'review' (warning icon,
// added at the render site). The 'not_started' state has NO config entry by
// design — it renders no badge (AC5). 'review' is the additive over-length chip
// shown alongside in_progress / complete (AC6), never on its own. Colors are
// deliberately distinct (blue / green / amber) so Review stays legible beside
// the primary badge and meaning never depends on color alone (AC11).
export const SESSION_CONTENT_STATUS_CONFIG: Record<string, StatusConfig> = {
  in_progress: { label: 'IN PROGRESS', color: 'blue', variant: 'light' },
  complete: { label: 'COMPLETE', color: 'medleyGreen', variant: 'light' },
  review: { label: 'REVIEW', color: 'warningAmber', variant: 'light' },
};

// Coach Availability Status Configuration
export const COACH_AVAILABILITY_CONFIG: Record<CohortCoachAvailabilityStatus, StatusConfig> = {
  available: { label: 'Available', color: 'medleyGreen', variant: 'light' },
  partially_available: { label: 'Partial', color: 'warningAmber', variant: 'light' },
  unavailable: { label: 'Unavailable', color: 'dangerRed', variant: 'light' },
  pending_response: { label: 'Pending Response', color: 'blue', variant: 'light' },
  not_requested: { label: 'Not Requested', color: 'blue', variant: 'light' },
};

// Milestone Status Configuration (overview progress bars)
export const MILESTONE_STATUS_CONFIG: Record<MilestoneStatus, { color: string }> = {
  completed: { color: 'var(--mantine-color-medleyGreen-6)' },
  in_progress: { color: 'var(--mantine-color-warningAmber-5)' },
  not_started: { color: 'var(--mantine-color-gray-3)' },
};

// Schedule Grid Cell Configuration (MED-333)
export interface ScheduleCellConfig {
  label: string;
  color: string;
  bgColor: string;
  borderColor?: string;
  borderStyle?: string;
}

export const SCHEDULE_CELL_CONFIG: Record<ScheduleCellState, ScheduleCellConfig> = {
  scheduled: {
    label: 'Scheduled',
    color: '#6C5CE7',
    bgColor: '#6C5CE7',
    borderColor: '#6C5CE7',
  },
  available: {
    label: 'Available',
    color: '#00B894',
    bgColor: 'rgba(0,184,148,0.08)',
    borderColor: '#00B894',
    borderStyle: 'dashed',
  },
  available_conditions: {
    label: 'Available',
    color: '#00B894',
    bgColor: 'rgba(0,184,148,0.08)',
    borderColor: '#00B894',
    borderStyle: 'dashed',
  },
  unavailable: {
    label: 'Unavailable',
    color: '#636E72',
    bgColor: '#DFE6E9',
  },
  awaiting_response: {
    label: 'Awaiting Response',
    color: '#E67E22',
    bgColor: '#FFF4E6',
  },
  not_requested: {
    label: 'Not Requested',
    color: '#B2BEC3',
    bgColor: '#FFFFFF',
    borderColor: '#DFE6E9',
    borderStyle: 'dashed',
  },
};

// Helper functions
export const getStatusLabel = <T extends string>(
  config: Record<T, StatusConfig>,
  status: T,
): string => config[status]?.label ?? status;

export const getStatusColor = <T extends string>(
  config: Record<T, StatusConfig>,
  status: T,
): string => config[status]?.color ?? 'gray';

// Convenience functions for specific types
export const getCoachStatusLabel = (status: CoachStatus): string =>
  getStatusLabel(COACH_STATUS_CONFIG, status);

export const getCoachStatusColor = (status: CoachStatus): string =>
  getStatusColor(COACH_STATUS_CONFIG, status);

export const getCohortStatusLabel = (status: CohortStatus): string =>
  getStatusLabel(COHORT_STATUS_CONFIG, status);

export const getCohortStatusColor = (status: CohortStatus): string =>
  getStatusColor(COHORT_STATUS_CONFIG, status);
